import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import useSettingsStore from '../store/useSettingsStore';
import { motion, AnimatePresence } from 'framer-motion';

const NAV_ITEMS = [
  { path: '/', label: 'Главная' },
  { path: '/catalog', label: 'Каталог' },
  { path: '/configurator', label: 'Подбор' },
  { path: '/services', label: 'Услуги' },
  { path: '/reviews', label: 'Отзывы' },
  { path: '/blog', label: 'Блог' },
  { path: '/about', label: 'О нас' },
  { path: '/contacts', label: 'Контакты' },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const settings = useSettingsStore(s => s.settings);
  const content = useSettingsStore(s => s.content);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  const companyName = content?.header?.company_name || settings?.site_title || 'AvtoPrime';
  const contactBtn = content?.header?.contact_button || 'Связаться';

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled ? 'bg-black/80 backdrop-blur-lg border-b border-white/5' : 'bg-transparent'
        }`}
      >
        <div className="container-main flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            {settings?.logo_url && (
              <img src={settings.logo_url} alt={companyName} className="h-14 w-14 md:h-16 md:w-16 rounded-full object-cover" fetchpriority="high" />
            )}
            <span className="text-xl md:text-2xl font-bold tracking-tight">{companyName}</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map(item => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  location.pathname === item.path
                    ? 'text-white bg-white/10'
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/contacts"
              data-testid="header-contact-btn"
              className="hidden md:inline-flex btn-primary rounded-lg text-sm"
            >
              {contactBtn}
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 text-white"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[var(--ap-bg)]/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col items-center justify-center h-full gap-2 pt-16">
              {NAV_ITEMS.map((item, i) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Link
                    to={item.path}
                    data-testid={`mobile-nav-${item.path.replace('/', '') || 'home'}`}
                    className={`block px-8 py-4 text-xl font-medium transition-colors ${
                      location.pathname === item.path ? 'text-[var(--ap-primary)]' : 'text-white/80'
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                <Link to="/contacts" className="btn-primary rounded-lg text-lg mt-4 inline-block">
                  {contactBtn}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
